import { Box, Skeleton, useToast } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useMemo, useState } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { SuccessPortfolioCovenantGQLType } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  covenantsInitialStartDate,
  defaultPortfolioCovenantValueType,
  ExpandedCovenantChartData,
  getAllUniqueCovenantKeys,
  PortfolioChartConfig,
  PortfolioChartFormat,
} from '../shared/portfolioChartUtils';
import { CovenantChartTemplate } from './CovenantChartTemplate';
import { useGetSuccessPortfolioCovenantsByCreditFacilityIdQuery } from 'packages/borrowing-base/requests/portfolioCovenants.gql.generated';
import { colors } from 'packages/common-frontend/colors';
import { InfoAlertWithDescription } from 'packages/common-frontend/components/InfoAlertWithDescription';
import {
  errorToastConfig,
  getDefaultDashboardDisplayEndDate,
} from 'packages/common-frontend/constants';
import { useQueryParams } from 'packages/common-frontend/hooks/useQueryParams';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetCurrentCaAndWip } from 'packages/creditAgreements/ui/creditAgreementWip/utils/useGetCurrentCaAndWip';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

const covenantsDisplayModes = ['all', 'portfolio', 'financial'] as const;
type CovenantsChartDisplayMode = typeof covenantsDisplayModes[number];
const isCovenantsChartDisplayMode = (
  test: any,
): test is CovenantsChartDisplayMode => {
  return (
    test && typeof test === 'string' && _.includes(covenantsDisplayModes, test)
  );
};

const covenantsDisplayModeQueryParam = 'display';

// Each config should be in the order that the chart components are layered
// ie. the last item is the top-most layer on render
export const createCovenantsChartConfig = (
  format: PortfolioChartFormat,
): PortfolioChartConfig<keyof ExpandedCovenantChartData> => {
  return {
    result: {
      chartType: 'none',
      field: 'result',
      label: 'Result',
      format: 'boolean',
      color: 'black',
    },
    value: {
      chartType: 'line',
      field: 'value',
      label: 'Value',
      format,
      color: colors.chart.yellow,
    },
  };
};

type CovenantsContainerProps = {
  creditFacilityId: string;
};
export const CovenantsContainer = ({
  creditFacilityId,
}: CovenantsContainerProps) => {
  const flags = useFeatureFlags();
  const toast = useToast();
  const query = useQueryParams();
  const queryDisplayMode = query.get(covenantsDisplayModeQueryParam);
  const displayMode = isCovenantsChartDisplayMode(queryDisplayMode)
    ? queryDisplayMode
    : 'all';

  const [
    allPortfolioCovenantsInDefaultRange,
    setAllPortfolioCovenantsInDefaultRange,
  ] = useState<SuccessPortfolioCovenantGQLType[]>([]);

  const {
    loading: isPortfolioCovenantsLoading,
    data: allPortfolioCovenantsData,
    refetch: refetchSuccessPortfolioCovenants,
  } = useGetSuccessPortfolioCovenantsByCreditFacilityIdQuery({
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    onCompleted: (data) => {
      if (data.getSuccessPortfolioCovenantsByCreditFacilityId.length) {
        // set the initial set so all configs within the longer initial range are considered
        if (!allPortfolioCovenantsInDefaultRange.length) {
          setAllPortfolioCovenantsInDefaultRange(
            data.getSuccessPortfolioCovenantsByCreditFacilityId,
          );
        }
      }
    },
    variables: {
      creditFacilityId,
      mode: 'RESULT',
      startDate: covenantsInitialStartDate,
      endDate: getDefaultDashboardDisplayEndDate(),
    },
  });

  const { currentCreditAgreementFullArtifact } = useGetCurrentCaAndWip({
    creditFacilityId,
    creditAgreementsPath: '/',
  });

  const uniquePortfolioCovenantKeys = useMemo(() => {
    if (allPortfolioCovenantsInDefaultRange.length) {
      return getAllUniqueCovenantKeys(allPortfolioCovenantsInDefaultRange);
    }
    return [];
  }, [allPortfolioCovenantsInDefaultRange]);

  if (
    isPortfolioCovenantsLoading ||
    !allPortfolioCovenantsData ||
    !currentCreditAgreementFullArtifact
  ) {
    return <Skeleton height="600px" data-testid="covenant-insights-loading" />;
  }

  if (
    !allPortfolioCovenantsData.getSuccessPortfolioCovenantsByCreditFacilityId
      .length
  ) {
    return (
      <InfoAlertWithDescription description="No covenant calculations are available in the selected time period." />
    );
  }

  const { portfolioCovenants } = currentCreditAgreementFullArtifact;

  return (
    <Box>
      {(displayMode === 'all' || displayMode === 'portfolio') &&
        flags.displayPortfolioCovenants && (
          <>
            {uniquePortfolioCovenantKeys.map((portfolioCovenantKey) => {
              const creditAgreementPortfolioCovenant = _.find(
                portfolioCovenants,
                (portfolioCovenant) =>
                  portfolioCovenant.covenantKey === portfolioCovenantKey,
              );

              // per product, we only want to show portfolio covenants that are present in the current published credit agreement
              if (!creditAgreementPortfolioCovenant) {
                return null;
              }

              const valueType =
                creditAgreementPortfolioCovenant?._config_?.valueType ??
                defaultPortfolioCovenantValueType;

              return (
                <CovenantChartTemplate
                  covenantType="portfolio"
                  creditFacilityId={creditFacilityId}
                  chartTitle={commonUtils.toTitleCase(portfolioCovenantKey)}
                  covenantKey={portfolioCovenantKey}
                  chartConfig={createCovenantsChartConfig(valueType)}
                  key={`portfolio-covenant-chart-${portfolioCovenantKey}`}
                  creditAgreementCovenant={creditAgreementPortfolioCovenant}
                  covenantData={
                    allPortfolioCovenantsData.getSuccessPortfolioCovenantsByCreditFacilityId
                  }
                  refetchCovenants={refetchSuccessPortfolioCovenants}
                  valueType={valueType}
                />
              );
            })}
          </>
        )}
    </Box>
  );
};
